import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Box } from '@tink/core/src/components/atoms/Box/Box';
import { Caption } from '../../../../../atoms/Text/Text';
import { InformationField } from '../../../../../molecules/InformationField/InformationField';
import { PageView } from '../../../../../../models/analytics';
import { useAnalytics } from '../../../../../../core/Analytics/AnalyticsProvider';
import { LoadingSpinner } from '../../../../../atoms/LoadingSpinner/LoadingSpinner';
import { H1 } from '../../../../../atoms/Heading/Heading';
import { ApplicationUnverifiedLabel } from '../../../../../molecules/ApplicationUnverifiedLabel/ApplicationUnverifiedLabel';
import { Stack } from '../../../../../atoms/Stack/Stack';
import { UnderlinedTextButton } from '../../../../../atoms/Button/TextButton';
import { Header } from '../../../../../organisms/Header/Header';
import { ContentBody } from '../../../../../atoms/ContentBody/ContentBody';
import { ConsentIcon } from '../../../../../atoms/Icons/Consent/ConsentIcon';
import { StyledError } from '../../SubmitCredentialsScreen.styles';
import { messages as submitCredentialsMessage } from '../../../../SubmitCredentials/messages';
import { messages as legalDisclaimerMessage } from '../../../LegalDisclaimer/LegalDisclaimerAuth/messages';
import { Footer } from '../../../../../atoms/Footer/Footer';
import { Button } from '../../../../../atoms/Button/Button';
import {
  createSubmitCredentialsScreen,
  SubmitCredentialsScreenCreatorProps,
} from '../../components/SubmitCredentialsScreenCreator/SubmitCredentialsScreenCreator';
import { SubmitCredentialsBase } from '../../components/SubmitCredentialsBase/SubmitCredentialsBase';
import { StyledForm } from '../../../../SubmitCredentials/molecules/ProviderInputs/ProviderInputs.styles';
import { ScopesDetailOverlay } from '../../../LegalDisclaimer/LegalDisclaimerAuth/molecules/ScopesDetailOverlay/ScopesDetailOverlay';
import { messages as consentScreenMessages } from './messages';

export const getConsentScreenHeader: SubmitCredentialsScreenCreatorProps['getHeader'] = (
  onBack,
  HeaderBase = Header
) => {
  return ({ provider, ...headerProps }) => <HeaderBase addBottomMargin {...headerProps} />;
};

export const getConsentScreenForm: SubmitCredentialsScreenCreatorProps['getForm'] = (onSubmit) => {
  return ({
    credentialsError,
    isClientVerified,
    isCredentialsLoading,
    clientName = '',
    scopesDescriptionsList = [],
  }) => {
    const { formatMessage } = useIntl();
    const { tinklinkAnalytics } = useAnalytics();
    const [isScopesDetailOverlayOpen, setScopesOverlayOpen] = useState(false);
    const openScopesModal = (event: { preventDefault: () => void }) => {
      event.preventDefault();
      setScopesOverlayOpen(true);
    };

    const closeScopesModal = () => setScopesOverlayOpen(false);
    const formId = 'consent-submit';

    useEffect(() => {
      tinklinkAnalytics.sendViewEvent({
        view: PageView.CONSENT_SCREEN,
      });
    }, [tinklinkAnalytics]);

    const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      onSubmit({ fields: {} });
    };

    return (
      <ContentBody centerVertically centerHorizontally data-testid="tpp-consent-screen">
        <ApplicationUnverifiedLabel isVerified={Boolean(isClientVerified)} />
        <Stack justifyContent="center">
          <Box mt="normal" mb="small">
            <ConsentIcon />
          </Box>
          <Box mx="large" mb="xxsmall">
            <H1 align="center">
              <FormattedMessage
                {...consentScreenMessages.consentTitle}
                values={{
                  clientName,
                }}
              />
            </H1>
          </Box>
          <InformationField
            iconVariant="lock"
            informationText={formatMessage(consentScreenMessages.consentInstruction)}
            isDecorated={false}
            iconColor="primary"
          />
          <InformationField iconVariant="profile" isDecorated={false} iconColor="primary">
            <Caption>
              <FormattedMessage
                {...consentScreenMessages.consentDetails}
                values={{
                  clientName,
                }}
              />{' '}
              <UnderlinedTextButton onClick={openScopesModal} as="a" role="button" tabIndex={0}>
                <FormattedMessage {...legalDisclaimerMessage.readMore} />
              </UnderlinedTextButton>
              {isScopesDetailOverlayOpen && (
                <ScopesDetailOverlay close={closeScopesModal} scopes={scopesDescriptionsList} />
              )}
            </Caption>
          </InformationField>
          {credentialsError && <StyledError>{credentialsError}</StyledError>}
        </Stack>

        <Footer stickToBottom>
          <StyledForm id={formId} onSubmit={handleSubmit} data-testid={formId}>
            {isCredentialsLoading ? (
              <LoadingSpinner />
            ) : (
              <Button
                type="submit"
                color="primary"
                variant="contained"
                data-testid="submit-credentials-button"
                data-test="submitCredentialsButton"
              >
                <FormattedMessage {...submitCredentialsMessage.continue} />
              </Button>
            )}
          </StyledForm>
        </Footer>
      </ContentBody>
    );
  };
};

export const ConsentScreen = createSubmitCredentialsScreen({
  SubmitCredentialsBase,
  getHeader: getConsentScreenHeader,
  getForm: getConsentScreenForm,
});
